export type DisplayQuotaWindow = {
	key: string;
	label: string;
	usagePercent?: number;
	resetAt?: number;
};

export type QuotaWindowSource = {
	provider: "codex" | "anthropic";
	primaryUsagePercent?: number;
	primaryResetAt?: number;
	secondaryUsagePercent?: number;
	secondaryResetAt?: number;
	quotaWindows: Array<{
		scopeKey: string;
		usagePercent?: number;
		resetAt?: number;
	}>;
};

const ANTHROPIC_SHARED_QUOTA_LABELS: Record<string, string> = {
	"shared:5h": "Session (5h)",
	"shared:7d": "Weekly (all models)",
};

const CODEX_PRIMARY_QUOTA_LABEL = "Weekly";
const CODEX_SECONDARY_QUOTA_LABEL = "GPT 5.3 Spark";

function quotaWindowLabel(scopeKey: string): string {
	const shared = ANTHROPIC_SHARED_QUOTA_LABELS[scopeKey];
	if (shared) return shared;
	const model = /^model:([^:]+):7d$/.exec(scopeKey);
	if (model) {
		const family = model[1];
		return `Weekly (${family.charAt(0).toUpperCase()}${family.slice(1)})`;
	}
	return scopeKey;
}

const ANTHROPIC_SCOPE_ORDER: Record<string, number> = {
	"shared:5h": 0,
	"shared:7d": 1,
};

export function displayQuotaWindows(
	account: QuotaWindowSource,
): DisplayQuotaWindow[] {
	if (account.provider === "anthropic") {
		const windows = account.quotaWindows
			.filter(
				(window) =>
					window.usagePercent !== undefined || window.resetAt !== undefined,
			)
			.sort(
				(a, b) =>
					(ANTHROPIC_SCOPE_ORDER[a.scopeKey] ?? 2) -
						(ANTHROPIC_SCOPE_ORDER[b.scopeKey] ?? 2) ||
					a.scopeKey.localeCompare(b.scopeKey),
			)
			.map((window) => ({
				key: window.scopeKey,
				label: quotaWindowLabel(window.scopeKey),
				usagePercent: window.usagePercent,
				resetAt: window.resetAt,
			}));
		if (windows.length > 0) return windows;
	}
	const primaryLabel =
		account.provider === "anthropic"
			? ANTHROPIC_SHARED_QUOTA_LABELS["shared:5h"]
			: CODEX_PRIMARY_QUOTA_LABEL;
	const secondaryLabel =
		account.provider === "anthropic"
			? ANTHROPIC_SHARED_QUOTA_LABELS["shared:7d"]
			: CODEX_SECONDARY_QUOTA_LABEL;
	const windows: DisplayQuotaWindow[] = [];
	if (
		account.primaryUsagePercent !== undefined ||
		account.primaryResetAt !== undefined
	) {
		windows.push({
			key: "primary",
			label: primaryLabel,
			usagePercent: account.primaryUsagePercent,
			resetAt: account.primaryResetAt,
		});
	}
	if (
		account.secondaryUsagePercent !== undefined ||
		account.secondaryResetAt !== undefined
	) {
		windows.push({
			key: "secondary",
			label: secondaryLabel,
			usagePercent: account.secondaryUsagePercent,
			resetAt: account.secondaryResetAt,
		});
	}
	return windows;
}
